import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const Pick = ({ label, value, onChange, items, testId }) => (
  <div className="space-y-1.5">
    <div className="text-sm font-medium text-slate-700">{label}</div>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="Velg kunde" /></SelectTrigger>
      <SelectContent>{items.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const SourceField = ({ info, onSaved }) => {
  const [url, setUrl] = useState(info?.available ? info.source : "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/sync/source", { url });
      toast.success("Adressen er lagret");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-1.5">
      <div className="text-sm font-medium text-slate-700">Adresse til preview-serveren</div>
      <div className="flex gap-2">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://navn.preview.emergentagent.com" data-testid="sync-source-url" />
        <Button variant="outline" onClick={save} disabled={busy || !url.trim()} data-testid="sync-source-save">Lagre</Button>
      </div>
      {!info?.available && <p className="text-xs text-amber-800" data-testid="sync-source-hint">Adressen peker nå til denne serveren ({info?.self}). Skriv inn adressen til preview-serveren og trykk Lagre.</p>}
    </div>
  );
};

export const SyncDialog = ({ open, onClose, customers, onDone, info, onSource }) => {
  const [src, setSrc] = useState([]);
  const [sid, setSid] = useState("");
  const [tid, setTid] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !info?.available) return;
    setPreview(null); setErr(""); setBusy(true);
    api.get("/admin/sync/source-customers").then((r) => {
      setSrc(r.data);
      const demo = r.data.find((c) => /demo/i.test(c.name)) || r.data[0];
      setSid(demo?.id || "");
      const t = customers.find((c) => !c.deleted && demo && c.name === demo.name);
      setTid(t?.id || "");
    }).catch((e) => setErr(errMsg(e))).finally(() => setBusy(false));
  }, [open, customers, info]);

  const run = async (confirm) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sync/import", { source_id: sid, target_id: tid, confirm });
      if (!confirm) { setPreview(data); return; }
      toast.success(`Innholdet fra «${data.source_name}» er importert til «${data.target_name}»`);
      onDone(); onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="sync-dialog">
        <DialogHeader>
          <DialogTitle>Importer innhold fra preview</DialogTitle>
          <DialogDescription>Kopierer kalkulator, innstillinger, logo, passasjerrapporter, rederier, anløpsliste, statistikk og endringslogg. Brukere og passord kopieres ikke.</DialogDescription>
        </DialogHeader>
        <SourceField key={`${info?.source}-${open}`} info={info} onSaved={onSource} />
        {!info?.available ? null : err ? <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md p-3" data-testid="sync-error">{err}</p> : (
          <div className="space-y-4">
            <Pick label="Hent fra (preview)" value={sid} onChange={(v) => { setSid(v); setPreview(null); }} items={src} testId="sync-source" />
            <Pick label="Overskriv (her)" value={tid} onChange={(v) => { setTid(v); setPreview(null); }} items={customers.filter((c) => !c.deleted)} testId="sync-target" />
            {preview && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="sync-preview">
                Alt innhold i «{preview.target_name}» erstattes med innholdet fra «{preview.source_name}» på preview:
                {" "}{preview.counts.pax_submissions} passasjerrapporter, {preview.counts.pax_actors} rederier og {preview.counts.audit_logs} logglinjer. Dette kan ikke angres.
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="sync-cancel">Avbryt</Button>
          {info?.available && !err && (preview
            ? <Button onClick={() => run(true)} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="sync-confirm">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Importer og overskriv</Button>
            : <Button onClick={() => run(false)} disabled={busy || !sid || !tid} data-testid="sync-check">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Fortsett</Button>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
